<template>
    <div class="body v2-list-page" :class="{ 'quick-view-open': quickViewVisible }" :style="quickViewOffsetStyle">
        <div class="order-page-container">
            <div class="page-section">
                <a-card :bordered="false" style="width: 100%" class="v2-page-card" id="title-card">
                    <template slot="title">
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item>
                                <router-link to="/cliente/monitores/cotacoes">
                                    Home
                                </router-link>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item>
                                <router-link to="/requisicoes">
                                    Requisições
                                </router-link>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item class="order-page-title">
                                Requisição #{{ id }}
                            </a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template slot="extra">
                        <div class="title-actions">
                            <a-tag v-if="stockSurvey" class="status-tag" :color="stockSurvey.color">{{ stockSurvey.label }}</a-tag>
                            <a-tag v-if="order" class="status-tag" :color="statusOf(order.status).color">{{ statusOf(order.status).label }}</a-tag>
                            <a-dropdown v-if="order" :trigger="['click']">
                                <a-button>
                                    Outras Funções
                                    <a-icon type="down" />
                                </a-button>
                                <a-menu slot="overlay">
                                    <a-menu-item @click="exportExcel">
                                        <i class="fa-solid fa-file-excel"></i>
                                        Exportar Excel
                                    </a-menu-item>
                                    <a-menu-item v-if="order.checkin_id && canOpenVisit" @click="$router.push(`/visitas/${order.checkin_id}`)">
                                        <i class="fa-solid fa-store"></i>
                                        Abrir visita
                                    </a-menu-item>
                                    <a-menu-item v-if="cancelable" @click="cancel">
                                        <i class="fa-solid fa-ban"></i>
                                        Cancelar requisição
                                    </a-menu-item>
                                </a-menu>
                            </a-dropdown>
                            <a-button v-if="editable" :loading="saving" @click="save">
                                <i v-if="!saving" class="fa-solid fa-arrow-left"></i>
                                Voltar
                            </a-button>
                            <a-button v-if="pending" type="danger" :disabled="busy" @click="showReject = true">
                                <i class="fa-solid fa-xmark"></i>
                                Rejeitar
                            </a-button>
                            <a-button v-if="pending" type="primary" :loading="approving" :disabled="busy" @click="approve">
                                <i v-if="!approving" class="fa-solid fa-check"></i>
                                Aprovar
                            </a-button>
                        </div>
                    </template>

                    <div v-if="!order && !loading" class="empty-text">Requisição não encontrada.</div>
                    <template v-else-if="order">
                        <div class="request-header">
                            <a-row :gutter="[12, 12]">
                                <a-col :xs="24" :sm="12" :xl="6">
                                    <div class="request-title-display">
                                        <span class="request-title-main">{{ order.store ? order.store.name : "--" }}</span>
                                        <span v-if="order.store" class="request-title-sub">{{ order.store.city }}</span>
                                    </div>
                                </a-col>
                                <a-col :xs="24" :sm="12" :xl="6">
                                    <div class="request-title-display">
                                        <span class="request-title-main">{{ order.provider ? order.provider.name : "--" }}</span>
                                        <span v-if="order.seller" class="request-title-sub">{{ order.seller.name }}</span>
                                    </div>
                                </a-col>
                                <a-col :xs="24" :sm="12" :xl="6">
                                    <div class="request-title-display">
                                        <span class="request-title-main">{{ brl(order.totals.amount) }}</span>
                                        <span class="request-title-sub">
                                            {{ order.totals.offered }} {{ order.totals.offered === 1 ? "produto" : "produtos" }}<template v-if="order.totals.priced"> · {{ order.totals.priced }} com preço</template><template v-if="order.totals.shortage"> · {{ order.totals.shortage }} em falta</template>
                                        </span>
                                    </div>
                                </a-col>
                                <a-col :xs="24" :sm="12" :xl="6">
                                    <div class="request-title-display">
                                        <span class="request-title-main">{{ order.sent_at ? formatDateTime(order.sent_at) : "Em digitação" }}</span>
                                        <span v-if="order.sent_at" class="request-title-sub">enviada</span>
                                    </div>
                                </a-col>
                            </a-row>
                        </div>
                        <PageTabs :tabs="tabs" :active="activeTab" @change="openTab" />
                    </template>
                </a-card>
            </div>

            <ProductsTab v-if="loading" key="products-loading" :order="emptyOrder" loading />

            <template v-else-if="order">
                <ProductsTab
                    v-if="activeTab === 'PRODUCTS'"
                    :key="`products-${order.id}`"
                    ref="products"
                    :order="order"
                    :lines="lines"
                    :meta="linesMeta"
                    :erp="erpByCode"
                    :refreshing-erp="refreshingErp"
                    :has-erp="hasErp"
                    :erp-refreshable="erpRefreshable"
                    @refresh-erp="refreshErp"
                    :loading="productsLoading"
                    :editable="editable"
                    :client-owned="clientOwned"
                    @query="loadLines"
                    @line-saved="onLineSaved"
                    @line-added="onLineAdded"
                    @line-removed="onLineRemoved"
                    @giro="openProductQuickView"
                    @cell-focus="handleCellFocus"
                />
                <VisitTab v-if="activeTab === 'VISIT'" :order="order" />
                <DeliveryTab v-if="activeTab === 'DELIVERY'" :order="order" :editable="editable" :saving="savingDelivery" @save="saveDelivery" />
                <DecisionTab v-if="activeTab === 'DECISION'" :order="order" />
            </template>
        </div>

        <RejectModal v-if="showReject && order" :order="order" @rejected="onDecided" @close="showReject = false" />
        <SellerPickModal v-if="showSellerPick && order" :provider="order.provider" @select="pickSeller" @close="showSellerPick = false" />

        <SuggestionAlgorithmModal
            :value="suggestionAlgorithm"
            :visible="algorithmModalVisible"
            :hidden-fields="['exibicao', 'unidade']"
            @reset="suggestionAlgorithm = { ...DEFAULT_SUGGESTION_ALGORITHM }"
            @close="algorithmModalVisible = false"
            @save="confirmErpRefresh"
        />

        <ProductQuickViewVertical
            :visible="quickViewVisible"
            :width="quickViewWidth"
            :product="quickViewProduct"
            :last-purchase="lastPurchase"
            :stock="stock"
            :last-sale="lastSale"
            :sales-analysis="salesAnalysis"
            :chart-labels="chartLabels"
            :sales-points="salesPoints"
            :purchase-points="purchasePoints"
            :sales-highlights="salesHighlights"
            :purchase-highlights="purchaseHighlights"
            :purchases="purchases"
            :network-sales="networkSales"
            :network-total="networkTotal"
            :stores="quickViewStores"
            :selected-store-id="selectedStoreId"
            store-locked
            :has-navigation="hasQuickViewNavigation"
            :can-go-previous="canGoPreviousQuickViewProduct"
            :can-go-next="canGoNextQuickViewProduct"
            :current-index="quickViewSiblingIndex"
            :total-count="quickViewSiblings ? quickViewSiblings.length : 0"
            :read-only="true"
            :data-unavailable="quickViewDataUnavailable"
            :loading="quickViewLoading"
            :diagnostics-stores="diagnosticsStores"
            :updated-at="updatedAt"
            @close="closeProductQuickView"
            @expand="openQuickViewWindow"
            @previous-product="goToPreviousQuickViewProduct"
            @next-product="goToNextQuickViewProduct"
        />
        <ProductQuickViewWindow
            :visible="quickViewWindowVisible"
            :product="quickViewProduct"
            :last-purchase="lastPurchase"
            :stock="stock"
            :last-sale="lastSale"
            :sales-analysis="salesAnalysis"
            :sales-points="salesPoints"
            :purchase-points="purchasePoints"
            :purchases="purchases"
            :network-sales="networkSales"
            :network-total="networkTotal"
            :stores="quickViewStores"
            :selected-store-id="selectedStoreId"
            store-locked
            :has-navigation="hasQuickViewNavigation"
            :can-go-previous="canGoPreviousQuickViewProduct"
            :can-go-next="canGoNextQuickViewProduct"
            :current-index="quickViewSiblingIndex"
            :total-count="quickViewSiblings ? quickViewSiblings.length : 0"
            :read-only="true"
            :data-unavailable="quickViewDataUnavailable"
            :loading="quickViewLoading"
            :diagnostics-stores="diagnosticsStores"
            :updated-at="updatedAt"
            @close="closeQuickViewWindow"
            @collapse="collapseQuickViewWindow"
            @previous-product="goToPreviousQuickViewProduct"
            @next-product="goToNextQuickViewProduct"
        />
    </div>
</template>

<script>
import SellerOrderService from "@/services/v3/seller-orders/seller-order.service";
import PageTabs from "@/components/cliente/monitors/PageTabs.vue";
import StoreService from "@/services/v1/store.service";
import ProductQuickViewVertical from "@/pages/monitors/winners-v2/components/modals/ProductQuickViewVertical.vue";
import ProductQuickViewWindow from "@/pages/monitors/winners-v2/components/modals/ProductQuickViewWindow.vue";
import { productQuickViewMixin } from "@/mixins/product-quick-view.mixin";
import MiraiService, { suggestionCodeKey, buildSuggestionAlgoritmo } from "@/services/MiraiService";
import SuggestionAlgorithmModal from "@/modals/diagnosis/SuggestionAlgorithmModal.vue";
import { getStockStatus } from "@/pages/monitors/winners-v2/components/modals/product-quick-view/stockStatus";
import visitFormat from "@/pages/visits/visit-format.mixin";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import { ALL_SELLER_ORDER_STATUS, CANCELABLE_STATUSES, FINAL_STATUSES, STOCK_SURVEY, isStockSurvey } from "./seller-order-status";
import { has_feature, VISITS } from "@/helpers/features";
import ProductsTab, { defaultLinesQuery, effectiveOf } from "./tabs/_products.tab.vue";
import VisitTab from "./tabs/_visit.tab.vue";
import DeliveryTab from "./tabs/_delivery.tab.vue";
import { deliveryComplete } from "./delivery";
import DecisionTab from "./tabs/_decision.tab.vue";
import RejectModal from "./modals/reject.modal.vue";
import SellerPickModal from "./modals/seller-pick.modal.vue";

const SUGGESTION_ALGORITHM_STORAGE_KEY = "resupply_suggestion_algorithm";

const DEFAULT_SUGGESTION_ALGORITHM = {
    estoque: "real",
    pendencia: "desconsiderar",
    cobertura: "tempo_compra",
    cobertura_dias: 30,
    giro: "ponderado",
    unidade: "unidade",
    exibicao: "todos",
};

const loadSuggestionAlgorithm = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(SUGGESTION_ALGORITHM_STORAGE_KEY));
        return saved && typeof saved === "object" ? { ...DEFAULT_SUGGESTION_ALGORITHM, ...saved } : { ...DEFAULT_SUGGESTION_ALGORITHM };
    } catch (error) {
        return { ...DEFAULT_SUGGESTION_ALGORITHM };
    }
};

export default {
    name: "SellerOrderPage",
    components: { PageTabs, ProductsTab, VisitTab, DeliveryTab, DecisionTab, RejectModal, SellerPickModal, ProductQuickViewVertical, ProductQuickViewWindow, SuggestionAlgorithmModal },
    mixins: [visitFormat, loaderMixin, productQuickViewMixin],
    props: ["id"],
    data() {
        return {
            service: new SellerOrderService(),
            statusMap: ALL_SELLER_ORDER_STATUS,
            order: null,
            loading: false,
            activeTab: this.$route.query.tab,
            showReject: false,
            showSellerPick: false,
            approving: false,
            saving: false,
            savingDelivery: false,
            busy: false,
            emptyOrder: { products: [] },
            lines: [],
            linesMeta: null,
            linesQuery: null,
            legacyLines: false,
            linesSeq: 0,
            productsLoading: false,
            allStores: [],
            storeNumber: undefined,
            erpByCode: {},
            hasErp: false,
            refreshingErp: false,
            DEFAULT_SUGGESTION_ALGORITHM,
            suggestionAlgorithm: loadSuggestionAlgorithm(),
            algorithmModalVisible: false,
            pendingRefreshLineIds: null,
            cellFocusTimer: null,
        };
    },
    computed: {
        pending() {
            if (!this.order) return false;
            if (this.order.status === "SENT") return true;
            if (this.order.status !== "DRAFT") return false;
            return !this.order.checkin_id || (this.manual && !!(this.order.visit && this.order.visit.checked_out_at));
        },
        erpRefreshable() {
            return !!this.order && !FINAL_STATUSES.includes(this.order.status);
        },
        cancelable() {
            return !!this.order && CANCELABLE_STATUSES.includes(this.order.status);
        },
        manual() {
            return !!this.order && this.order.origin === "CLIENT_MANUAL";
        },
        stockSurvey() {
            return isStockSurvey(this.order) ? STOCK_SURVEY : null;
        },
        canOpenVisit() {
            return has_feature(VISITS);
        },
        clientOwned() {
            return !!this.order && (!this.order.checkin_id || this.manual);
        },
        editable() {
            if (!this.order || this.order.decided_at) return false;
            return this.order.status === "SENT" || (this.clientOwned && this.order.status === "DRAFT");
        },
        tabs() {
            const order = this.order;
            return [
                { key: "PRODUCTS", label: "Produtos", icon: "inventory_2", badge: order ? order.totals.offered : null, visible: true },
                { key: "VISIT", label: "Visita", icon: "storefront", visible: !!order && !!order.checkin_id },
                { key: "DELIVERY", label: "Entrega", icon: "local_shipping", visible: !!order && !!order.delivery },
                {
                    key: "DECISION",
                    label: "Decisão",
                    icon: order && order.status === "REJECTED" ? "block" : "task_alt",
                    visible: !!order && ["APPROVED", "REJECTED"].includes(order.status),
                },
            ].filter((tab) => tab.visible);
        },
    },
    watch: {
        "$route.query.tab"(tab) {
            this.activeTab = tab;
            this.ensureValidTab();
        },
        id: "load",
    },
    created() {
        this.load();
    },
    beforeDestroy() {
        clearTimeout(this.cellFocusTimer);
    },
    methods: {
        load() {
            this.loading = true;
            this.linesQuery = null;
            this.storeNumber = undefined;
            this.erpByCode = {};
            this.fetch()
                .finally(() => {
                    this.loading = false;
                })
                .then(() => this.autoRefreshErp());
        },
        autoRefreshErp() {
            if (!this.hasErp || !this.erpRefreshable) return;
            this.refreshStoredErp(null, buildSuggestionAlgoritmo(loadSuggestionAlgorithm()), true);
        },
        fetch() {
            return this.service
                .show(this.id)
                .then(({ data }) => {
                    this.order = data;
                    this.ensureValidTab();
                    return this.loadLines(this.linesQuery || defaultLinesQuery(data));
                })
                .catch((error) => {
                    this.order = null;
                    if (!error.response || error.response.status !== 404) this.notifyError(error, "Não foi possível carregar a requisição.");
                });
        },
        loadLines(query) {
            this.linesQuery = query;
            const seq = ++this.linesSeq;
            this.productsLoading = true;
            const request = this.legacyLines ? this.legacyLinesRequest(query) : this.service.products(this.id, query);
            return request
                .then(({ data }) => {
                    if (seq !== this.linesSeq) return;
                    this.applyLines(data);
                })
                .catch((error) => {
                    if (seq !== this.linesSeq) return;
                    // LEGACY: back sem GET /requisitions/{id}/products devolve as linhas embutidas no show. Remover quando o endpoint de linhas estiver em produção.
                    if (!this.legacyLines && error.response && [404, 405].includes(error.response.status) && this.order && Array.isArray(this.order.products)) {
                        this.legacyLines = true;
                        this.applyLines(this.order);
                        return;
                    }
                    this.notifyError(error, "Não foi possível carregar os produtos.");
                })
                .finally(() => {
                    if (seq === this.linesSeq) this.productsLoading = false;
                });
        },
        legacyLinesRequest(query) {
            const filters = Object.keys(query)
                .filter((key) => key.startsWith("filter["))
                .reduce((params, key) => ({ ...params, [key]: query[key] }), {});
            return this.service.show(this.id, filters);
        },
        applyLines(data) {
            if (this.legacyLines) {
                this.lines = data.products || [];
                this.linesMeta = null;
                if (data.totals) this.order = { ...this.order, totals: data.totals };
                this.loadErp(this.lines);
                return;
            }
            this.lines = data.data || [];
            this.linesMeta = data.meta || null;
            if (data.totals) this.order = { ...this.order, totals: data.totals };
            if (this.lines.some((line) => "erp" in line)) this.hasErp = true;
            // LEGACY: back sem `erp` nas linhas, o estoque e o custo vem direto do mirai. Remover quando o kyojin com product_store_erp_metrics estiver em produção.
            if (!this.hasErp) this.loadErp(this.lines);
        },
        async ensureStoreNumber() {
            if (this.storeNumber !== undefined) return this.storeNumber;
            if (!this.allStores.length) {
                try {
                    const { data } = await new StoreService().all(this.$store.state.user.cli_id);
                    this.allStores = (data && data.lojas) || [];
                } catch (error) {
                    this.notifyError(error, "Não foi possível carregar as lojas.");
                    return null;
                }
            }
            const current = this.order && this.order.store && this.allStores.find((store) => String(store.loj_id) === String(this.order.store.id));
            this.storeNumber = current && current.loj_numeroLoja ? current.loj_numeroLoja : null;
            return this.storeNumber;
        },
        async refreshErp(lines) {
            if (this.hasErp) {
                this.pendingRefreshLineIds = lines ? lines.map((line) => line.id) : null;
                this.suggestionAlgorithm = loadSuggestionAlgorithm();
                this.algorithmModalVisible = true;
                return;
            }
            const codes = lines.map((line) => suggestionCodeKey(line.product.internal_code)).filter(Boolean);
            if (!codes.length) {
                this.snack("Os produtos selecionados não têm código interno.", "error");
                return;
            }
            this.refreshingErp = true;
            const updated = await this.loadErp(lines, true);
            this.refreshingErp = false;
            if (updated === null) this.snack("Não foi possível atualizar estoque e custo.", "error");
            else this.snack(updated ? `Estoque e custo atualizados para ${updated} produto(s).` : "Nenhum produto foi encontrado no ERP.");
        },
        confirmErpRefresh() {
            this.algorithmModalVisible = false;
            const algoritmo = buildSuggestionAlgoritmo(this.suggestionAlgorithm);
            try {
                localStorage.setItem(SUGGESTION_ALGORITHM_STORAGE_KEY, JSON.stringify(algoritmo));
            } catch (error) {
                // sem localStorage, a escolha vale so para esta atualizacao
            }
            this.refreshStoredErp(this.pendingRefreshLineIds, algoritmo);
        },
        refreshStoredErp(lineIds, algorithm, silent = false) {
            const id = this.id;
            this.refreshingErp = true;
            return this.service
                .refreshErp(id, lineIds, algorithm)
                .then(({ data }) => {
                    if (id !== this.id) return;
                    if (silent) return this.loadLines(this.linesQuery);
                    if (!data.updated && !data.missing) this.snack("Nenhum produto pode ser consultado no ERP: sem código interno ou loja sem número cadastrado.", "error");
                    else if (!data.updated) this.snack("Nenhum produto foi encontrado no ERP.", "error");
                    else this.snack(`Dados do ERP atualizados para ${data.updated} produto(s).`);
                    return this.loadLines(this.linesQuery);
                })
                .catch((error) => {
                    if (!silent) this.notifyError(error, "Não foi possível atualizar os dados do ERP.");
                })
                .finally(() => {
                    this.refreshingErp = false;
                });
        },
        async loadErp(lines, force = false) {
            const codes = [...new Set(lines.map((line) => suggestionCodeKey(line.product.internal_code)).filter(Boolean))].filter((code) => force || !(code in this.erpByCode));
            if (!codes.length) return 0;
            const storeNumber = await this.ensureStoreNumber();
            const entries = {};
            codes.forEach((code) => {
                entries[code] = { loading: true };
            });
            this.erpByCode = { ...this.erpByCode, ...entries };
            const found = {};
            let failed = false;
            if (storeNumber) {
                try {
                    const { data } = await new MiraiService(this.$store.state.user.cli_id).stockCost(codes);
                    (data.data || [])
                        .filter((row) => String(row.store_number).trim() === String(storeNumber).trim())
                        .forEach((row) => {
                            found[suggestionCodeKey(row.internal_code)] = { stock: row.stock, last_cost: row.last_cost };
                        });
                } catch (error) {
                    failed = true;
                    codes.forEach((code) => {
                        found[code] = { failed: true };
                    });
                }
            }
            const done = {};
            codes.forEach((code) => {
                done[code] = found[code] || {};
            });
            this.erpByCode = { ...this.erpByCode, ...done };
            return failed ? null : Object.keys(found).length;
        },
        openTab(key) {
            this.activeTab = key;
            if (this.$route.query.tab !== key) {
                this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {});
            }
        },
        ensureValidTab() {
            if (!this.tabs.some((tab) => tab.key === this.activeTab)) {
                this.activeTab = this.tabs.length ? this.tabs[0].key : undefined;
            }
        },
        onLineSaved({ line, totals }) {
            this.lines = this.lines.map((item) => (item.id === line.id ? { ...item, ...line } : item));
            this.order = { ...this.order, totals };
        },
        toQuickViewProduct(line) {
            return {
                id: line.product.id,
                name: line.product.description,
                ean: line.product.ean,
                internal_code: line.product.internal_code,
                packing: line.product.packing,
                source: line,
            };
        },
        async loadStores() {
            const number = await this.ensureStoreNumber();
            const current = number && this.allStores.find((store) => String(store.loj_id) === String(this.order.store.id));
            this.quickViewStores = current ? [{ id_loja: number, loja_descricao: current.loj_descricao }] : [];
            this.selectedStoreId = number || null;
        },
        async openProductQuickView(line, siblings) {
            this.quickViewSiblings = siblings.map(this.toQuickViewProduct);
            this.quickViewSiblingIndex = siblings.findIndex((item) => item.id === line.id);
            this.quickViewProduct = this.toQuickViewProduct(line);
            this.quickViewReadonly = true;
            await this.loadStores();
            if (!this.selectedStoreId) {
                this.snack("A loja desta requisição não tem número cadastrado, não é possível exibir o giro.", "error");
                return;
            }
            this.quickViewVisible = true;
            await this.loadQuickViewData();
        },
        handleCellFocus(line, siblings) {
            if (!this.quickViewVisible && !this.quickViewWindowVisible) return;
            clearTimeout(this.cellFocusTimer);
            this.cellFocusTimer = setTimeout(() => {
                if (this.quickViewProduct && this.quickViewProduct.id === line.product.id) return;
                this.quickViewSiblings = siblings.map(this.toQuickViewProduct);
                this.goToQuickViewProductAt(siblings.findIndex((item) => item.id === line.id));
            }, 500);
        },
        async goToQuickViewProductAt(index) {
            const next = this.quickViewSiblings && this.quickViewSiblings[index];
            if (!next) return;
            this.quickViewSiblingIndex = index;
            this.quickViewProduct = next;
            await this.loadQuickViewData();
        },
        onLineAdded({ totals }) {
            this.order = { ...this.order, totals };
            this.loadLines(this.linesQuery);
        },
        onLineRemoved({ id, totals }) {
            this.order = { ...this.order, totals };
            if (!this.linesMeta) {
                this.lines = this.lines.filter((item) => item.id !== id);
                return;
            }
            const lastOnPage = this.lines.length === 1 && this.linesMeta.current_page > 1;
            this.loadLines(lastOnPage ? { ...this.linesQuery, page: this.linesMeta.current_page - 1 } : this.linesQuery);
        },
        save() {
            const products = this.$refs.products;
            this.saving = true;
            Promise.resolve(products ? products.flush() : true)
                .then((ok) => {
                    if (!ok) return;
                    this.snack("Requisição salva.");
                    this.goBack();
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onDecided(result) {
            this.order = { ...this.order, ...result };
            this.fetch().then(() => this.openTab("DECISION"));
        },
        saveDelivery(payload) {
            this.savingDelivery = true;
            this.service
                .update(this.order.id, payload)
                .then(({ data }) => {
                    this.order = { ...this.order, delivery: data.delivery };
                    this.snack("Condições de entrega salvas.");
                })
                .catch((error) => this.notifyError(error, "Não foi possível salvar as condições de entrega."))
                .finally(() => {
                    this.savingDelivery = false;
                });
        },
        cancel() {
            this.confirm_action({
                message: "Cancelar esta requisição?",
                subtitle: "Ela continua na lista como cancelada e não pode mais ser alterada nem aprovada.",
                confirm_text: "Sim, cancelar",
                cancel_text: "Voltar",
                callback: () => {
                    this.busy = true;
                    this.service
                        .cancel(this.order.id)
                        .then(() => {
                            this.snack("Requisição cancelada.");
                            return this.fetch();
                        })
                        .catch((error) => this.notifyError(error, "Não foi possível cancelar a requisição."))
                        .finally(() => {
                            this.busy = false;
                        });
                },
            });
        },
        approve() {
            if (this.order.delivery && !deliveryComplete(this.order.delivery)) {
                this.snack("Informe as condições de entrega antes de aprovar.", "error");
                this.openTab("DELIVERY");
                return;
            }
            if (!this.order.seller) {
                this.showSellerPick = true;
                return;
            }
            this.confirmApproval(null);
        },
        pickSeller(seller) {
            this.showSellerPick = false;
            this.confirmApproval(seller);
        },
        confirmApproval(seller) {
            const shortage = this.order.totals.shortage || 0;
            const subtitle = `${this.order.totals.offered} produtos · ${this.brl(this.order.totals.amount)} de ${this.order.provider ? this.order.provider.name : "fornecedor"}${seller ? `, com o vendedor ${seller.label}` : ""}.`;
            const sellerId = seller ? seller.value : null;
            if (!shortage) {
                this.confirm_action({
                    message: "Aprovar esta requisição?",
                    subtitle,
                    confirm_text: "Sim, aprovar",
                    cancel_text: "Voltar",
                    callback: () => this.sendApproval(false, false, sellerId),
                });
                return;
            }
            this.$swal
                .fire({
                    title: "Aprovar esta requisição?",
                    text: subtitle,
                    icon: "question",
                    input: "checkbox",
                    inputValue: 1,
                    inputPlaceholder: `Gerar ressuprimento com ${shortage === 1 ? "o produto em falta" : `os ${shortage} produtos em falta`}`,
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: "Aprovar",
                    denyButtonText: "Aprovar e ver ressuprimento",
                    cancelButtonText: "Voltar",
                    confirmButtonColor: "#3085d6",
                    denyButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    returnInputValueOnDeny: true,
                    didOpen: () => {
                        const checkbox = this.$swal.getInput();
                        const deny = this.$swal.getDenyButton();
                        checkbox.addEventListener("change", () => {
                            deny.style.display = checkbox.checked ? "" : "none";
                        });
                    },
                })
                .then((result) => {
                    if (!result.isConfirmed && !result.isDenied) return;
                    const resupply = !!result.value;
                    this.sendApproval(resupply, result.isDenied && resupply, sellerId);
                });
        },
        sendApproval(resupply, openResupply, sellerId) {
            this.approving = true;
            this.busy = true;
            this.service
                .approve(this.order.id, resupply, sellerId)
                .then(({ data }) => {
                    if (openResupply && data.resupply_id) {
                        this.snack("Requisição aprovada. Ajuste as quantidades do ressuprimento.");
                        this.$router.push({ name: "Ressuprimento-Editar", params: { id: data.resupply_id } });
                        return;
                    }
                    this.snack(data.resupply_id ? `Requisição aprovada. Ressuprimento #${data.resupply_id} gerado.` : "Requisição aprovada.");
                    this.onDecided(data);
                })
                .catch((error) => this.notifyError(error, "Não foi possível aprovar a requisição."))
                .finally(() => {
                    this.approving = false;
                    this.busy = false;
                });
        },
        async allLines() {
            if (this.legacyLines) return this.lines;
            const query = { ...this.linesQuery, per_page: 200 };
            const lines = [];
            let page = 1;
            let lastPage = 1;
            do {
                const { data } = await this.service.products(this.id, { ...query, page });
                lines.push(...(data.data || []));
                lastPage = (data.meta && data.meta.last_page) || 1;
                page += 1;
            } while (page <= lastPage);
            return lines;
        },
        async exportExcel() {
            if (this.legacyLines) {
                this.legacyExportExcel();
                return;
            }
            const { page, per_page, ...query } = this.linesQuery || {};
            let response;
            try {
                response = await this.service.exportProducts(this.id, query);
            } catch (error) {
                this.notifyError(error, "Não foi possível exportar os produtos.");
                return;
            }
            // LEGACY: back sem export=xlsx devolve a listagem em JSON; a planilha é montada aqui. Remover quando o kyojin com o export estiver em produção.
            if (response.data.type && response.data.type.includes("json")) {
                this.legacyExportExcel();
                return;
            }
            const url = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `requisicao-${this.order.id}.xlsx`;
            link.click();
            URL.revokeObjectURL(url);
        },
        async legacyExportExcel() {
            let lines;
            try {
                lines = await this.allLines();
            } catch (error) {
                this.notifyError(error, "Não foi possível exportar os produtos.");
                return;
            }
            const XLSX = require("sheetjs-style");
            const order = this.order;
            const rows = [
                [`Requisição #${order.id}`, this.statusOf(order.status).label],
                ["Loja", order.store ? order.store.name : ""],
                ["Fornecedor", order.provider ? order.provider.name : ""],
                ["Vendedor", order.seller ? order.seller.name : ""],
                ["Enviada em", this.formatDateTime(order.sent_at)],
                [],
                [
                    "EAN",
                    "Código interno",
                    "Produto",
                    "Embalagem",
                    "Qtd. sugerida",
                    "Qtd. pedida",
                    "Preço ofertado",
                    "Preço pedido",
                    "Total",
                    "Observação",
                    ...(this.hasErp ? ["Situação", "Estoque", "Giro (un/dia)", "Sugestão", "Últ. custo", "Margem (%)"] : []),
                ],
                ...lines.map((line) => [
                    line.product.ean,
                    line.product.internal_code,
                    line.product.description,
                    line.product.packing,
                    line.quantity,
                    effectiveOf(line, "quantity"),
                    line.price,
                    effectiveOf(line, "price"),
                    line.amount !== undefined ? line.amount : Math.round(effectiveOf(line, "price") * effectiveOf(line, "quantity") * 100) / 100,
                    line.observation || "",
                    ...(this.hasErp ? this.erpExportCells(line.erp) : []),
                ]),
                ["", "", "", "", "", "", "", "Total", order.totals.amount],
            ];
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(rows), `Requisição ${order.id}`);
            XLSX.writeFile(workbook, `requisicao-${order.id}.xlsx`);
        },
        erpExportCells(erp) {
            if (!erp) return ["", "", "", "", "", ""];
            return [
                erp.criticality ? getStockStatus(erp.criticality).label : "",
                erp.stock,
                erp.giro,
                erp.suggestion,
                erp.last_cost,
                erp.margin,
            ];
        },
        goBack() {
            if (window.history.length > 1) this.$router.back();
            else this.$router.push("/requisicoes");
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/pages/visits/_page";

.body {
    --quick-view-width: 500px;
}

.body.quick-view-open {
    padding-right: var(--quick-view-width);
    box-sizing: border-box;
}

.request-header {
    padding: 16px 24px 4px;
}

.request-title-display {
    padding: 4px 11px;
    display: flex;
    flex-direction: column;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.request-title-main {
    font-size: 16px;
    color: rgba(0, 0, 0, 0.85);
}

.request-title-sub {
    font-size: 13px;
    color: #8c8c8c;
}

::v-deep .page-tabs {
    border-top: 1px solid #f0f0f0;
}

.empty-text {
    padding: 24px;
    text-align: center;
    color: #8c8c8c;
}

.status-tag {
    align-self: center;
}
</style>
